// ✅ src/pages/VehicleV2/VehicleTransferReceiving.jsx  (UPDATED FULL FILE)
// Mount assumed:
//   app.use("/api/vehicleTransferReceiving", vehicleTransferReceiving);
//
// Backend endpoints used:
//   GET  https://nissi-india.com/api/vehicleTransferReceiving/init?branchId=&branchType=&loginUserId=
//   GET  https://nissi-india.com/api/vehicleTransferReceiving/list?...filters...
//   POST https://nissi-india.com/api/vehicleTransferReceiving/receive/vehicles
//   POST https://nissi-india.com/api/vehicleTransferReceiving/receive/spares
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + gridLoading + submitting states
// - cleaner init / refresh / filter / list / receive flow
// - fixed stale-state issue in View All
// - safer effects to avoid unnecessary repeated API calls

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaFilter,
  FaArrowLeft,
  FaCheckCircle,
  FaSyncAlt,
  FaUndo,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleTransferReceiving";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const clean = (v) => String(v ?? "").trim();

const selectValue = (v) => {
  const s = clean(v);
  return s ? s : "Select";
};

const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const MONTHS = [
  "Select",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const defaultFilters = {
  vehicleMode: "own", // own | spares
  locationType: "B", // B | L
  vehicleCode: "Select",
  vehicleNo: "Select",
  receivedFrom: "Select",
  storeId: "Select",
  locationId: "Select",
  status: "Select",
  month: "Select",
  year: "Select",
  vehicleMainTypeIndex: "Select",
};

const VehicleReceivingEntry = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [showFilters, setShowFilters] = useState(true);

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [filters, setFilters] = useState(defaultFilters);

  const [opts, setOpts] = useState({
    years: [{ label: "Select", value: "Select" }],
    months: MONTHS.map((m) => ({ label: m, value: m })),
    stores: [{ label: "Select", value: "Select" }],
    locations: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
    vehicleNos: [{ label: "Select", value: "Select" }],
    receivedFrom: [{ label: "Select", value: "Select" }],
    status: [
      { label: "Select", value: "Select" },
      { label: "Pending", value: "0" },
      { label: "Received", value: "1" },
    ],
  });

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(rows.length / pageSize)),
    [rows.length]
  );

  const paginatedRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page]
  );

  const isBusy = pageLoading || dropdownLoading || gridLoading || submitting;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetFiltersOnly = useCallback(() => {
    setFilters((prev) => ({
      ...defaultFilters,
      vehicleMode: prev.vehicleMode,
      locationType: prev.locationType,
    }));
  }, []);

  const buildListParams = useCallback(
    (override = {}) => ({
      branchType,
      branchId,
      loginUserId,

      vehicleMode: override.vehicleMode ?? filters.vehicleMode,
      locationType: override.locationType ?? filters.locationType,

      status: selectValue(override.status ?? filters.status),
      receivedFrom: selectValue(override.receivedFrom ?? filters.receivedFrom),
      vehicleCode: selectValue(override.vehicleCode ?? filters.vehicleCode),
      vehicleNo: selectValue(override.vehicleNo ?? filters.vehicleNo),
      storeId: selectValue(override.storeId ?? filters.storeId),
      locationId: selectValue(override.locationId ?? filters.locationId),
      month: selectValue(override.month ?? filters.month),
      year: selectValue(override.year ?? filters.year),
      vehicleMainType: selectValue(
        override.vehicleMainTypeIndex ?? filters.vehicleMainTypeIndex
      ),
    }),
    [branchId, branchType, loginUserId, filters]
  );

  /* =========================================================
     API LOADERS
     ========================================================= */
  const loadDropdowns = useCallback(async () => {
    const { data } = await axios.get(`${API_BASE}/init`, {
      params: {
        branchId,
        branchType,
        loginUserId,
      },
      headers: commonHeaders,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to load initial dropdowns");
    }

    const years = toList(data.years).map((y) => ({
      label: String(y),
      value: String(y),
    }));

    const stores = toList(data.stores).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const locations = toList(data.locations).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const vehicleTypes = [
      { label: "Select", value: "Select" },
      ...toList(data.vehicleTypes)
        .filter(
          (x) =>
            x?.fld_vehicletypeindex !== undefined &&
            x?.fld_vehicletypeindex !== null &&
            x?.fld_vehicletypename !== undefined &&
            x?.fld_vehicletypename !== null
        )
        .map((x) => ({
          label: String(x.fld_vehicletypename),
          value: String(x.fld_vehicletypeindex),
        })),
    ];

    const vehicleCodes = toList(data.vehicleCodeList).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const vehicleNos = toList(data.vehicleRegNoList).map((x) => ({
      label: String(x.label ?? "Select"),
      value: String(x.value ?? "Select"),
    }));

    const receivedFrom = [
      { label: "Select", value: "Select" },
      ...toList(data.receivedFromList)
        .filter((x) => x?.fld_vehicle_tobranchid != null)
        .map((x) => ({
          label: String(x.fld_BranchName ?? ""),
          value: String(x.fld_vehicle_tobranchid),
        })),
    ];

    setOpts((prev) => ({
      ...prev,
      years: years.length ? years : [{ label: "Select", value: "Select" }],
      stores: stores.length ? stores : [{ label: "Select", value: "Select" }],
      locations: locations.length
        ? locations
        : [{ label: "Select", value: "Select" }],
      vehicleTypes:
        vehicleTypes.length > 0
          ? vehicleTypes
          : [{ label: "Select", value: "Select" }],
      vehicleCodes: vehicleCodes.length
        ? vehicleCodes
        : [{ label: "Select", value: "Select" }],
      vehicleNos: vehicleNos.length
        ? vehicleNos
        : [{ label: "Select", value: "Select" }],
      receivedFrom:
        receivedFrom.length > 0
          ? receivedFrom
          : [{ label: "Select", value: "Select" }],
    }));
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadList = useCallback(
    async ({ silent = false, overrideFilters = null } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(overrideFilters || {}),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          setPage(1);
          if (!silent) toast.warn(data?.message || "No records found");
          return;
        }

        const uiRows = toList(data.rows).map((r, idx) => ({
          __rowId: idx + 1,
          __checked: false,
          __receiveDate: todayDDMMYYYY(),
          ...r,
        }));

        setRows(uiRows);
        setPage(1);

        if (!silent) {
          toast.success(`Found ${uiRows.length} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setPage(1);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, commonHeaders, buildListParams, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      setDropdownLoading(true);
      await loadDropdowns();
      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setDropdownLoading(false);
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  /* ---------------- row handlers ---------------- */
  const toggleRow = useCallback((rowId, checked) => {
    setRows((prev) =>
      prev.map((r) => (r.__rowId === rowId ? { ...r, __checked: checked } : r))
    );
  }, []);

  const setRowDate = useCallback((rowId, date) => {
    setRows((prev) =>
      prev.map((r) =>
        r.__rowId === rowId ? { ...r, __receiveDate: date } : r
      )
    );
  }, []);

  /* ---------------- actions ---------------- */
  const handleSearch = useCallback(async () => {
    await loadList({ silent: false });
  }, [loadList]);

  const handleViewAll = useCallback(async () => {
    const resetObj = {
      ...defaultFilters,
      vehicleMode: filters.vehicleMode,
      locationType: filters.locationType,
    };

    setFilters(resetObj);

    await loadList({
      silent: false,
      overrideFilters: resetObj,
    });
  }, [filters.vehicleMode, filters.locationType, loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const receiveSelected = useCallback(async () => {
    const selected = rows.filter((r) => r.__checked);

    if (!selected.length) {
      toast.info("Please select equipment to receive");
      return;
    }

    const missingDate = selected.find((r) => !toStr(r.__receiveDate));
    if (missingDate) {
      toast.error("Please enter receiving date");
      return;
    }

    try {
      setSubmitting(true);

      if (filters.vehicleMode === "spares") {
        const payload = {
          branchId,
          items: selected.map((r) => ({
            transferId:
              r.fld_spare_trandferid ??
              r.fld_spare_transferid ??
              r.fld_vehicle_trandferid ??
              r.transferId ??
              r.id ??
              r.fld_id,
            receiveDate: r.__receiveDate,
          })),
        };

        const { data } = await axios.post(
          `${API_BASE}/receive/spares`,
          payload,
          { headers: commonHeaders }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Receive failed");
        }

        toast.success(data.message || "Spares received successfully");
      } else {
        const payload = {
          branchId,
          loginUserId,
          items: selected.map((r) => ({
            transferId:
              r.fld_vehicle_trandferid ??
              r.fld_vehtransferid ??
              r.fld_transferid ??
              r.transferId ??
              r.id ??
              r.fld_id,
            receiveDate: r.__receiveDate,
          })),
        };

        const { data } = await axios.post(
          `${API_BASE}/receive/vehicles`,
          payload,
          { headers: commonHeaders }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Receive failed");
        }

        toast.success(data.message || "Equipments received successfully");
      }

      await loadList({ silent: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Receive failed"
      );
    } finally {
      setSubmitting(false);
    }
  }, [rows, filters.vehicleMode, branchId, loginUserId, commonHeaders, loadList]);

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    // reload grid silently only when mode/location type changes
    loadList({ silent: true });
  }, [filters.vehicleMode, filters.locationType]); // eslint-disable-line react-hooks/exhaustive-deps

  const recordsCount = useMemo(() => rows.length, [rows.length]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE TRANSFER RECEIVING
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Receiving Entry
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {filters.vehicleMode === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                <span className={pillClass}>
                  Branch Type: {branchType || "-"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
                title="Refresh"
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Mode Section */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="own"
                    checked={filters.vehicleMode === "own"}
                    onChange={(e) => setField("vehicleMode", e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    value="spares"
                    checked={filters.vehicleMode === "spares"}
                    onChange={(e) => setField("vehicleMode", e.target.value)}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Spares & Catalogue
                  </span>
                </label>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                {/* Branch / Location */}
                <div className="mb-6">
                  <label className={labelClass}>Filter By</label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        value="B"
                        checked={filters.locationType === "B"}
                        onChange={(e) => setField("locationType", e.target.value)}
                        className="form-radio text-blue-600"
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Branch
                      </span>
                    </label>

                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        value="L"
                        checked={filters.locationType === "L"}
                        onChange={(e) => setField("locationType", e.target.value)}
                        className="form-radio text-blue-600"
                        disabled={isBusy}
                      />
                      <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                        Location
                      </span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className={labelClass}>Vehicle Code</label>
                    <select
                      className={inputClass}
                      value={filters.vehicleCode}
                      onChange={(e) => setField("vehicleCode", e.target.value)}
                      disabled={isBusy || filters.vehicleMode === "spares"}
                    >
                      {opts.vehicleCodes.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Status</label>
                    <select
                      className={inputClass}
                      value={filters.status}
                      onChange={(e) => setField("status", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.status.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Received From</label>
                    <select
                      className={inputClass}
                      value={filters.receivedFrom}
                      onChange={(e) => setField("receivedFrom", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.receivedFrom.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Number</label>
                    <select
                      className={inputClass}
                      value={filters.vehicleNo}
                      onChange={(e) => setField("vehicleNo", e.target.value)}
                      disabled={isBusy || filters.vehicleMode === "spares"}
                    >
                      {opts.vehicleNos.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      className={inputClass}
                      value={filters.storeId}
                      onChange={(e) => setField("storeId", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.stores.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Location</label>
                    <select
                      className={inputClass}
                      value={filters.locationId}
                      onChange={(e) => setField("locationId", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.locations.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Month</label>
                    <select
                      className={inputClass}
                      value={filters.month}
                      onChange={(e) => setField("month", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.months.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Year</label>
                    <select
                      className={inputClass}
                      value={filters.year}
                      onChange={(e) => setField("year", e.target.value)}
                      disabled={isBusy}
                    >
                      {opts.years.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Vehicle Main Type</label>
                    <select
                      className={inputClass}
                      value={filters.vehicleMainTypeIndex}
                      onChange={(e) =>
                        setField("vehicleMainTypeIndex", e.target.value)
                      }
                      disabled={isBusy}
                    >
                      {opts.vehicleTypes.map((o, i) => (
                        <option key={i} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={resetFiltersOnly}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>

                    <button
                      onClick={receiveSelected}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                    >
                      <FaCheckCircle />
                      {submitting ? "Receiving..." : "Receive Selected"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Transfer Receiving List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {filters.vehicleMode === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{rows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center">Select</th>
                    <th className="px-3 py-3 text-center">S.No</th>
                    <th className="px-3 py-3 text-center">Transfer ID</th>
                    <th className="px-3 py-3 text-center">Vehicle Code</th>
                    <th className="px-3 py-3 text-center">Vehicle No</th>
                    <th className="px-3 py-3 text-center">Received From</th>
                    <th className="px-3 py-3 text-center">From Location</th>
                    <th className="px-3 py-3 text-center">Receiving Date</th>
                    <th className="px-3 py-3 text-center">Status</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((r, idx) => {
                      const transferId =
                        r.fld_vehicle_trandferid ??
                        r.fld_spare_trandferid ??
                        r.fld_transferid ??
                        r.transferId ??
                        "";

                      const code =
                        r.fld_vehicle_code ?? r.vehicleCode ?? r.code ?? "-";

                      const regNo =
                        r.fld_vehicle_regno ??
                        r.fld_vehicleno ??
                        r.vehicleNo ??
                        r.number ??
                        "-";

                      const recvFrom =
                        r.fld_BranchName ?? r.receivedFrom ?? "-";

                      const fromLoc =
                        r.fld_Worklocationname ?? r.fromLocation ?? "-";

                      const inward =
                        r.fld_vehicle_inward ??
                        r.fld_inward ??
                        r.status ??
                        r.fld_status ??
                        "";

                      const isReceived =
                        inward === 1 ||
                        inward === "1" ||
                        inward === true ||
                        inward === "True";

                      return (
                        <tr
                          key={`${transferId || r.__rowId}-${idx}`}
                          className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                        >
                          <td className="px-3 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!r.__checked}
                              disabled={isBusy || isReceived}
                              onChange={(e) =>
                                toggleRow(r.__rowId, e.target.checked)
                              }
                            />
                          </td>

                          <td className="px-3 py-3 text-center">
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {transferId || "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {code || "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {regNo || "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {recvFrom || "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {fromLoc || "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {isReceived ? (
                              <span className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}>
                                {r.fld_inwarddate || r.receivedDate || "-"}
                              </span>
                            ) : (
                              <input
                                className={`px-2 py-1 border rounded text-sm ${
                                  isDark
                                    ? "bg-gray-800 border-gray-700 text-white"
                                    : "bg-white border-gray-300 text-gray-900"
                                }`}
                                value={r.__receiveDate}
                                onChange={(e) =>
                                  setRowDate(r.__rowId, e.target.value)
                                }
                                placeholder="dd/MM/yyyy"
                                disabled={isBusy}
                              />
                            )}
                          </td>

                          <td
                            className={`px-3 py-3 text-center text-sm font-semibold ${
                              isReceived ? "text-green-600" : "text-yellow-600"
                            }`}
                          >
                            {isReceived ? "Received" : "Pending"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {rows.length > 0 && (
              <div className="flex justify-center mt-6">
                <nav className="flex items-center space-x-2">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      disabled={isBusy}
                      className={`px-3 py-1 border rounded ${
                        page === i + 1
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "bg-gray-800 text-white border-gray-700"
                          : "bg-white text-gray-700 border-gray-300"
                      } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdowns..."
                  : gridLoading
                  ? "Loading grid..."
                  : submitting
                  ? "Submitting receive action..."
                  : "Loading..."}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleReceivingEntry;